import { EVENT_TYPE_NAMES } from '../../common/analytics/amplitudeEventTypesUtil';
import { constructAmplitudeEvent } from '../../common/analytics/constructAmplitudeEvent';
import { ELEMENT_CREATE } from '../../common/elements/elementConstants';
import { ElementType } from '../../common/elements/elementTypes';
import { asObject } from '../../common/utils/immutableHelper';
import { removeUserAnalyticsTracker, updateUserAnalyticsTracker } from '../../common/users/userAnalyticsActions';
import {
    MNUserAnalytics,
    MNUserAnalyticsTrackerState,
    UserAnalyticsTrackerType,
} from '../../common/users/userAnalyticsTypes';
import { ImMap, ImmutableOrPoJO } from '../../common/utils/immutableHelper';
import { sendAmplitudeEvent } from './amplitudeService';
import { BATCH_ACTION_TYPE } from '../store/reduxBulkingMiddleware';
import { TEMPLATE_PICKER_TEMPLATE_APPLIED } from '../workspace/boardTemplatePickerPopup/boardTemplateActions';
import { ReduxStore } from '../types/reduxTypes';

type CreateAction = {
    creationSource?: string;
    duplicate?: boolean;
    elementType?: string;
    isRedo?: boolean;
    isUndo?: boolean;
    location?: object;
    payload?: CreateAction[];
    remote?: boolean;
    type?: string;
};

type LogicalCreateEvent = {
    containsImage: boolean;
    imageCreateAction?: CreateAction;
    representativeCreateAction: CreateAction;
};

type ElementCreateMilestoneTrackingUpdate = {
    eventTypes: string[];
    removedTrackerTypes: UserAnalyticsTrackerType[];
    updatedTracker?: { trackerType: UserAnalyticsTrackerType; state: MNUserAnalyticsTrackerState };
    representativeCreateAction: CreateAction;
    imageCreateAction?: CreateAction;
};

type ElementCreateMilestoneAnalytics = ImMap<MNUserAnalytics> | MNUserAnalytics | undefined;

const getLogicalCreateEvent = (action?: CreateAction): LogicalCreateEvent | null => {
    if (!action || action.remote || action.isUndo || action.isRedo || action.duplicate) return null;

    if (action.type === TEMPLATE_PICKER_TEMPLATE_APPLIED) {
        return {
            containsImage: false,
            representativeCreateAction: {
                type: ELEMENT_CREATE,
                creationSource: action.creationSource,
                elementType: action.elementType,
                location: action.location,
            },
        };
    }

    if (action.type === ELEMENT_CREATE) {
        const containsImage = action.elementType === ElementType.IMAGE_TYPE;

        return {
            containsImage,
            imageCreateAction: containsImage ? action : undefined,
            representativeCreateAction: action,
        };
    }

    if (action.type !== BATCH_ACTION_TYPE || !action.payload?.length) return null;

    return action.payload.reduce<LogicalCreateEvent | null>((logicalCreateEvent, payloadAction) => {
        const nestedLogicalCreateEvent = getLogicalCreateEvent(payloadAction);
        if (!nestedLogicalCreateEvent) return logicalCreateEvent;

        if (!logicalCreateEvent) return nestedLogicalCreateEvent;

        return {
            containsImage: logicalCreateEvent.containsImage || nestedLogicalCreateEvent.containsImage,
            imageCreateAction: logicalCreateEvent.imageCreateAction || nestedLogicalCreateEvent.imageCreateAction,
            representativeCreateAction: logicalCreateEvent.representativeCreateAction,
        };
    }, null);
};

/**
 * Determines whether an action should advance the persisted create milestones and which
 * milestone events should be fired from that logical create event.
 */
export const getElementCreateMilestoneTrackingUpdate = ({
    action,
    userAnalytics,
}: {
    action?: CreateAction;
    userAnalytics: ElementCreateMilestoneAnalytics;
}): ElementCreateMilestoneTrackingUpdate | null => {
    const analytics = asObject<MNUserAnalytics>(userAnalytics as ImmutableOrPoJO) || {};
    if (!Object.keys(analytics).length) return null;

    const logicalCreateEvent = getLogicalCreateEvent(action);
    if (!logicalCreateEvent) return null;

    const hasFirstElementTracker = !!analytics[UserAnalyticsTrackerType.FIRST_ELEMENT_CREATION];
    const thirdElementTracker = analytics[UserAnalyticsTrackerType.THIRD_ELEMENT_CREATION];
    const hasFirstImageTracker = !!analytics[UserAnalyticsTrackerType.FIRST_IMAGE_CREATION];

    const currentCount = thirdElementTracker?.state?.elementCreationCount || 0;
    const nextElementCreationCount = thirdElementTracker ? currentCount + 1 : currentCount;

    const eventTypes: string[] = [];
    const removedTrackerTypes: UserAnalyticsTrackerType[] = [];
    let updatedTracker: ElementCreateMilestoneTrackingUpdate['updatedTracker'];

    if (hasFirstElementTracker) {
        removedTrackerTypes.push(UserAnalyticsTrackerType.FIRST_ELEMENT_CREATION);
        eventTypes.push(EVENT_TYPE_NAMES.ELEMENT_CREATION_FIRST);
    }

    if (thirdElementTracker) {
        if (nextElementCreationCount >= 3) {
            removedTrackerTypes.push(UserAnalyticsTrackerType.THIRD_ELEMENT_CREATION);
            eventTypes.push(EVENT_TYPE_NAMES.ELEMENT_CREATION_THIRD);
        } else {
            updatedTracker = {
                trackerType: UserAnalyticsTrackerType.THIRD_ELEMENT_CREATION,
                state: { elementCreationCount: nextElementCreationCount },
            };
        }
    }

    if (hasFirstImageTracker && logicalCreateEvent.containsImage) {
        removedTrackerTypes.push(UserAnalyticsTrackerType.FIRST_IMAGE_CREATION);
        eventTypes.push(EVENT_TYPE_NAMES.ELEMENT_CREATION_FIRST_IMAGE);
    }

    if (!eventTypes.length && !updatedTracker) return null;

    return {
        eventTypes,
        removedTrackerTypes,
        updatedTracker,
        representativeCreateAction: logicalCreateEvent.representativeCreateAction,
        imageCreateAction: logicalCreateEvent.imageCreateAction,
    };
};

export const trackElementCreateMilestones = (
    store: ReduxStore,
    action: CreateAction,
    userDetails: Record<string, unknown>,
    userAnalytics: ElementCreateMilestoneAnalytics,
) => {
    const milestoneUpdate = getElementCreateMilestoneTrackingUpdate({ action, userAnalytics });

    if (!milestoneUpdate) return;

    milestoneUpdate.eventTypes.forEach((eventType) => {
        const sourceAction =
            eventType === EVENT_TYPE_NAMES.ELEMENT_CREATION_FIRST_IMAGE
                ? milestoneUpdate.imageCreateAction || milestoneUpdate.representativeCreateAction
                : milestoneUpdate.representativeCreateAction;

        const amplitudeEventDetails = constructAmplitudeEvent({ event: sourceAction, userDetails });

        sendAmplitudeEvent({
            eventType,
            eventProperties: amplitudeEventDetails.event_properties,
            userProperties: amplitudeEventDetails.user_properties,
        });
    });

    milestoneUpdate.removedTrackerTypes.forEach((trackerType) => {
        store.dispatch(removeUserAnalyticsTracker({ trackerType }));
    });

    if (milestoneUpdate.updatedTracker) {
        store.dispatch(updateUserAnalyticsTracker(milestoneUpdate.updatedTracker));
    }
};
